import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { router } from "expo-router";

import { Spacings } from "@/styling/spacings";
import BaseItemCard from "@/Components/Cards/BaseItemCard";
import { themes } from "@/Components/Styling/themeConfigs";
import type { UserNotification } from "@/schemas/user/notification";
import UserAvatar from "@/Components/Media/User/Images/UserAvatar";
import { formatDateTimeDelta } from "@/formatting/datetime";
import type { UserProfilePreview } from "@/schemas/user/preview";

const currentTheme = themes.morning;

type Props = {
  renderRightComponent: (markAsRead: () => void) => React.ReactNode;
  userNotification: UserNotification;
  profile: UserProfilePreview; // TODO: use UserProfile type in schema
  handleMarkNotificationAsRead: () => void;
  notificationTypeIconComponent: React.ReactNode;
  notificationDescription: string;
};

function UserNotificationBaseItem({
  renderRightComponent,
  userNotification,
  profile,
  handleMarkNotificationAsRead,
  notificationTypeIconComponent,
  notificationDescription,
}: Props): React.JSX.Element {
  const [isRead, setIsRead] = useState(userNotification.isRead);

  // if markAllAsRead is called in the parent component, we need to update the state
  useEffect(() => {
    setIsRead(userNotification.isRead);
  }, [userNotification.isRead]);

  const markAsRead = async (): Promise<void> => {
    if (!isRead) {
      setIsRead(true);
      handleMarkNotificationAsRead();
    }
  };

  const handleUsernamePress = async (username: string): Promise<void> => {
    router.push({
      params: { username },
      pathname: "/(main)/shared/profile",
    });
  };

  const leftAvatarComponent = (
    <Pressable
      onPress={() => {
        markAsRead();
        handleUsernamePress(profile.username);
      }}
    >
      <UserAvatar
        avatar={profile.avatar}
        defaultAvatarColor={"black"}
        size={50}
      />
    </Pressable>
  );

  const infoComponent = (
    <View style={styles.infoContainer}>
      <View style={styles.detailItem}>
        <Pressable
          onPress={() => {
            markAsRead();
            handleUsernamePress(profile.username);
          }}
        >
          <Text style={styles.itemPrimaryText}>{profile.username}</Text>
        </Pressable>
        {notificationTypeIconComponent}
      </View>
      <View style={styles.detailItem}>
        <Text style={styles.itemSecondaryText}>{notificationDescription}</Text>
      </View>
      <View style={styles.detailItem}>
        <Text style={styles.itemDateTimeDeltaText}>
          {formatDateTimeDelta(userNotification.createdAt)}
        </Text>
      </View>
    </View>
  );

  return (
    <Pressable onPress={() => markAsRead()}>
      <BaseItemCard
        leftComponent={leftAvatarComponent}
        infoComponent={infoComponent}
        rightComponent={renderRightComponent(markAsRead)}
        additionalStyle={[
          styles.container,
          { backgroundColor: isRead ? "#fff" : "#f5f5f5" },
        ]}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: Spacings.s2,
    height: 85,
    overflow: "hidden",
    borderColor: currentTheme.borderColor,
    borderStyle: "solid",
    borderBottomWidth: 0.75,
  },
  infoContainer: {
    flex: 1,
    marginTop: 5,
  },
  detailItem: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3,
  },
  itemPrimaryText: {
    fontSize: currentTheme.typography.h2.size.mobile,
    fontWeight: 600, // TODO: change themeConfig to use number instead of string for weight?
    color: currentTheme.typography.h2.color,
    marginRight: 5,
  },
  itemSecondaryText: {
    fontSize: currentTheme.typography.body.size.mobile,
    color: "#222",
  },
  itemDateTimeDeltaText: {
    fontSize: currentTheme.typography.bodySmall.size.mobile,
    color: "#444",
  },
});

export default UserNotificationBaseItem;
